import { INITIAL_COUNTDOWN_TIME } from "./constants";
import { WindowWithTimerKey } from "./types";
import { TEST_TYPES } from "../../../../constants";

const windowWithTimer = window as unknown as WindowWithTimerKey;

const TEST_TYPES_TO_IGNORE_IN_COUNT = [TEST_TYPES.SUB_HEADING];

/**
 * Initializes a timer with the given key and initial time.
 * If a timer with the same key already exists, returns its current time.
 * @param timerKey - Unique identifier for the timer
 * @param initialTime - Initial countdown time in seconds (defaults to INITIAL_COUNTDOWN_TIME)
 * @returns Current timer value in seconds
 */
export function initializeTimer(
  timerKey: string,
  initialTime: number = INITIAL_COUNTDOWN_TIME
): number {
  if (!windowWithTimer[timerKey]) {
    windowWithTimer[timerKey] = {
      time: initialTime,
    };
  }
  return windowWithTimer[timerKey]!.time;
}

/**
 * Starts a countdown timer that decrements every second.
 * Automatically stops when reaching 0.
 * @param timerKey - Unique identifier for the timer
 * @param onTick - Callback function called every second with the current time
 * @returns Interval ID that can be used to clear the interval
 */
export function startCountdown(
  timerKey: string,
  onTick: (time: number) => void
): NodeJS.Timeout {
  // Clear any existing interval
  const timer = windowWithTimer[timerKey];
  if (timer?.intervalId) {
    clearInterval(timer.intervalId);
  }

  const intervalId = setInterval(() => {
    const currentTimer = windowWithTimer[timerKey];
    if (currentTimer && currentTimer.time > 0) {
      currentTimer.time -= 1;
      onTick(currentTimer.time);
    } else if (currentTimer?.intervalId) {
      clearInterval(currentTimer.intervalId);
      currentTimer.intervalId = undefined;
    }
  }, 1000);

  if (windowWithTimer[timerKey]) {
    windowWithTimer[timerKey]!.intervalId = intervalId;
  }

  return intervalId;
}

/**
 * Calculates the current question number and total questions count for a test set.
 * Excludes certain test types (like SUB_HEADING) from the count.
 * @param items - Array of test items
 * @param currentItemIndex - Current item index in the test set
 * @returns Object containing currentQuestion and totalQuestions
 */
export function calculateQuestionProgress(
  items: Array<{ type: string }>,
  currentItemIndex: number
): { currentQuestion: number; totalQuestions: number } {
  return items.reduce(
    (acc, item, index) => {
      const shouldCount = !TEST_TYPES_TO_IGNORE_IN_COUNT.includes(item.type);
      if (!shouldCount) {
        return acc;
      }

      const totalQuestions = acc.totalQuestions + 1;
      const currentQuestion =
        index <= currentItemIndex
          ? acc.currentQuestion + 1
          : acc.currentQuestion;

      return { totalQuestions, currentQuestion };
    },
    { totalQuestions: 0, currentQuestion: 0 }
  );
}

/**
 * Calculates the total progress and topic progress for non-market categories.
 * @param totalSets - Total number of test sets including market
 * @param uncompletedTestSetsCount - Number of uncompleted test sets
 * @param currentQuestion - Current question number (1-based)
 * @param totalQuestions - Total questions in current set
 * @returns Object containing totalProgress and topicProgress as percentages
 */
export function calculateTopicProgressForAnalytics(
  totalSets: number,
  uncompletedTestSetsCount: number,
  currentQuestion: number,
  totalQuestions: number
): { totalProgress: number; topicProgress: number } {
  const totalTopics = totalSets - 1; // -1 because market is passed
  const completedTopics = totalTopics - uncompletedTestSetsCount;
  const totalProgress = (completedTopics / totalTopics) * 100;
  const topicProgress =
    ((currentQuestion - 1) / totalQuestions / totalTopics) * 100;

  return { totalProgress, topicProgress };
}

export function calculateTopicProgressForReflection(
  totalSets: number,
  uncompletedTestSetsCount: number,
  currentQuestion: number,
  totalQuestions: number
): { totalProgress: number; topicProgress: number } {
  const totalTopics = totalSets;
  const completedTopics = totalTopics - uncompletedTestSetsCount;
  const totalProgress = (completedTopics / totalTopics) * 100;
  const topicProgress =
    ((currentQuestion - 1) / totalQuestions / totalTopics) * 100;

  return { totalProgress, topicProgress };
}
